// NATS connection singleton + JetStream consumer factory.
//
// Single connection per page lifetime, idempotent across React StrictMode
// double-effects, HMR, and route changes. Consumer lifecycle is owned by
// the caller (useJsConsumer); the connection itself isn't tied to any
// component's mount.
//
// Design rules (see wip-jetstream-cache.md):
//   - Rule 1: connection is module singleton, not React-owned
//   - Rule 8: proto decoding happens in the message callback, OFF the
//     React render path
//   - Rule 12: HMR dispose closes the existing connection cleanly
//   - Rule 14: zustand subscribe drops the connection on logout

import {
  connect,
  credsAuthenticator,
  type NatsConnection,
} from 'nats.ws';
import { useAuthStore } from '@/store/authStore';
import { useMachineStore } from '@/store/machineStore';
import { getCreds, clearCreds } from './credManager';
import { dropDedup, setPersistTenant } from './ingestQueue';
import { dropCache, closeCache } from '@/idb/cache';

let conn: Promise<NatsConnection> | null = null;

/**
 * Get the singleton NatsConnection, opening one if none exists. Concurrent
 * callers share one Promise so React StrictMode double-effect doesn't
 * cause a double-connect.
 */
export function getConnection(): Promise<NatsConnection> {
  if (!conn) {
    conn = openConnection().catch((err) => {
      conn = null;
      throw err;
    });
  }
  return conn;
}

/** Close the singleton connection if open. */
export async function dropConnection(): Promise<void> {
  if (!conn) return;
  const cur = conn;
  conn = null;
  try {
    const nc = await cur;
    await nc.drain();
  } catch {
    /* best-effort */
  }
}

async function openConnection(): Promise<NatsConnection> {
  const creds = await getCreds();
  // .creds bundle format mirrors what the bridge keyring produces. Built
  // in-memory; never written to disk.
  const credsBundle =
`-----BEGIN NATS USER JWT-----
${creds.jwt}
------END NATS USER JWT------

-----BEGIN USER NKEY SEED-----
${creds.seed}
------END USER NKEY SEED------
`;
  try {
    const nc = await connect({
      servers: creds.natsURL,
      authenticator: credsAuthenticator(new TextEncoder().encode(credsBundle)),
      name: 'dashboard-web',
      reconnect: true,
      maxReconnectAttempts: -1,
      reconnectTimeWait: 2_000,
      waitOnFirstConnect: true,
    });
    console.warn('[js] connected', { server: creds.natsURL, scope: creds.companySlug });
    return nc;
  } catch (err) {
    logSafeError('[js] connect failed', err);
    throw err;
  }
}

/** Log an error without leaking creds. Walks the error chain stripping
 *  any field whose key looks like a credential. Errors thrown by nats.ws
 *  are typically string messages so this rarely triggers, but the wrapper
 *  is the seatbelt. */
export function logSafeError(prefix: string, err: unknown): void {
  const sanitized = sanitize(err);
  console.error(prefix, sanitized);
}

const CRED_FIELDS = new Set(['jwt', 'seed', 'token', 'password', 'authorization', 'authenticator']);
function sanitize(v: unknown, depth = 0): unknown {
  if (depth > 4 || v == null) return v;
  if (typeof v === 'string') {
    return v.length > 200 ? v.slice(0, 200) + '…' : v;
  }
  if (typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((x) => sanitize(x, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    out[k] = CRED_FIELDS.has(k.toLowerCase()) ? '[redacted]' : sanitize(val, depth + 1);
  }
  return out;
}

// Consumer lifecycle moved to nats/api.ts (subscribeStream). This module
// only owns the connection singleton + auth-store wiring + HMR dispose.

// ── Auth-store wiring ────────────────────────────────────────────────────
//
// On logout, drop the connection + clear creds. Module-level subscription
// runs once at import time and lives for the page's lifetime — no React
// hook involvement, so this fires regardless of whether any component
// is mounted.
// The IDB cache is keyed per user (company + Zitadel sub) — see
// useJsConsumer's cacheScope — so a stale watermark from a prior user can't
// warm-resume the next user into a dead stream position. Mirror that key here
// when dropping/closing the cache on teardown.
function cacheScopeFor(
  company: string | null,
  user: { sub?: string; companySlug?: string | null } | null | undefined,
): string | null {
  const scope = company || user?.companySlug || null;
  if (!scope) return null;
  return user?.sub ? `${scope}::${user.sub}` : scope;
}

useAuthStore.subscribe((state, prev) => {
  const wasIn = !!prev.user?.token;
  const isIn  = !!state.user?.token;
  // Logout: tear down everything cleanly. Drop the IDB cache outright —
  // a different user logging into the same browser must not see the
  // previous user's data.
  if (wasIn && !isIn) {
    void dropConnection();
    clearCreds();
    dropDedup();
    setPersistTenant(null);
    useMachineStore.getState().reset();
    const cs = cacheScopeFor(prev.company, prev.user);
    if (cs) void dropCache(cs);
  }
  // Company switch OR same-company user swap — re-mint with the new scope and
  // drop the stale session (creds, dedup, machine store). A user swap shares
  // the company string but must not inherit the prior user's connection/creds
  // or warm-resume watermark. Close (not delete) the prior cache so a switch
  // back is a warm resume; evictExpired() keeps it bounded.
  const userChanged = state.user?.sub !== prev.user?.sub;
  if (state.company !== prev.company || (isIn && userChanged)) {
    void dropConnection();
    clearCreds();
    dropDedup();
    setPersistTenant(null);
    useMachineStore.getState().reset();
    const cs = cacheScopeFor(prev.company, prev.user);
    if (cs) void closeCache(cs);
  }
});

// ── HMR survival ─────────────────────────────────────────────────────────
//
// Vite HMR replaces this module on save; without dispose the dev server
// would accumulate dead NATS connections every save. dispose closes the
// current connection cleanly before the new module takes over.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error import.meta.hot is Vite-only and not in the default ImportMeta type
if (import.meta.hot) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  import.meta.hot.dispose(() => {
    void dropConnection();
    clearCreds();
    dropDedup();
  });
}
